import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Component from '../../components/Component';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import {
  getAnalyticsOverview,
  getAnalyticsTimeseries,
  getAnalyticsIntents,
  getAnalyticsTopMatches,
  getAnalyticsTopUsers,
  getAnalyticsMessages,
  getAnalyticsConversation,
  getAnalyticsModels,
  getAnalyticsIntentModelMatrix,
  getAnalyticsRoutingConfig,
  getAnalyticsMatchBreakdown,
  getAnalyticsMatchUsers,
  getAnalyticsMatchUserConversation,
} from '../../services/aiChatAnalyticsApi';

// Recharts pie palette — kept short, intent buckets are typically <12.
const PIE_COLORS = [
  '#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f',
  '#0288d1', '#7b1fa2', '#388e3c', '#f57c00', '#c62828',
  '#0097a7', '#5d4037',
];

function isoDateInput(d) {
  // YYYY-MM-DD for <input type="date" /> compatibility
  return d ? new Date(d).toISOString().slice(0, 10) : '';
}

function formatNumber(n) {
  if (n == null || isNaN(n)) return '0';
  return Number(n).toLocaleString();
}

function formatRelative(ts) {
  if (!ts) return '—';
  const diffMs = Date.now() - new Date(ts).getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function KpiCard({ label, value, sub }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
          {value}
        </Typography>
        {sub && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function AiChatDashboard() {
  // ── Filters (single source of truth for every endpoint) ────────────────
  const today = useMemo(() => new Date(), []);
  const sevenDaysAgo = useMemo(() => new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), [today]);
  const [filters, setFilters] = useState({
    from: isoDateInput(sevenDaysAgo),
    to: isoDateInput(today),
    match_id: '',
    user_id: '',
    intent: '',
    model: '',
  });
  const [pendingFilters, setPendingFilters] = useState(filters);
  const applyFilters = () => setFilters(pendingFilters);
  const resetFilters = () => {
    const fresh = { from: isoDateInput(sevenDaysAgo), to: isoDateInput(today), match_id: '', user_id: '', intent: '', model: '' };
    setPendingFilters(fresh);
    setFilters(fresh);
  };

  // Build a backend-friendly filter (ISO range with end-of-day on `to`)
  const apiFilters = useMemo(() => {
    const f = { ...filters };
    if (f.from) f.from = new Date(`${f.from}T00:00:00.000Z`).toISOString();
    if (f.to) f.to = new Date(`${f.to}T23:59:59.999Z`).toISOString();
    return f;
  }, [filters]);

  // ── Server data ────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [intents, setIntents] = useState([]);
  const [topMatches, setTopMatches] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  const [models, setModels] = useState([]);
  const [intentModelMatrix, setIntentModelMatrix] = useState([]);
  const [routingConfig, setRoutingConfig] = useState(null);

  // Message log (independent pagination)
  const [logPage, setLogPage] = useState(0);
  const [logLimit, setLogLimit] = useState(25);
  const [logSearch, setLogSearch] = useState('');
  const [logRoleFilter, setLogRoleFilter] = useState(''); // '', 'user', 'assistant'
  const [logFeedbackFilter, setLogFeedbackFilter] = useState(''); // '', 'liked', 'disliked'
  const [messageRows, setMessageRows] = useState([]);
  const [messageTotal, setMessageTotal] = useState(0);
  const [logLoading, setLogLoading] = useState(false);

  // Conversation drawer
  const [openConvo, setOpenConvo] = useState(null); // { sessionId, data }
  const [convoLoading, setConvoLoading] = useState(false);

  // ── Match-wise drilldown ───────────────────────────────────────────────
  const [matchBreakdownRows, setMatchBreakdownRows] = useState([]);
  const [matchBreakdownTotal, setMatchBreakdownTotal] = useState(0);
  const [matchBreakdownPage, setMatchBreakdownPage] = useState(0);
  const [matchBreakdownLimit, setMatchBreakdownLimit] = useState(25);
  const [matchBreakdownLoading, setMatchBreakdownLoading] = useState(false);

  // drilldown dialog state — `view` can be 'users' | 'conversation'
  const [drill, setDrill] = useState(null); // { view, match, users?, user_id?, conversation? }
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ov, ts, it, tm, tu, md, mx, rc] = await Promise.all([
        getAnalyticsOverview(apiFilters),
        getAnalyticsTimeseries(apiFilters),
        getAnalyticsIntents(apiFilters),
        getAnalyticsTopMatches({ ...apiFilters, limit: 20 }),
        getAnalyticsTopUsers({ ...apiFilters, limit: 20 }),
        getAnalyticsModels(apiFilters),
        getAnalyticsIntentModelMatrix(apiFilters),
        getAnalyticsRoutingConfig().catch(() => ({ data: null })),
      ]);
      setOverview(ov?.data || null);
      setSeries(ts?.data?.points || []);
      setIntents(it?.data?.intents || []);
      setTopMatches(tm?.data?.matches || []);
      setTopUsers(tu?.data?.users || []);
      setModels(md?.data?.models || []);
      setIntentModelMatrix(mx?.data?.rows || []);
      setRoutingConfig(rc?.data || null);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [apiFilters]);

  const fetchMessages = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = {
        ...apiFilters,
        limit: logLimit,
        offset: logPage * logLimit,
      };
      if (logSearch) params.q = logSearch;
      if (logRoleFilter) params.role = logRoleFilter;
      if (logFeedbackFilter === 'liked') params.liked = 'true';
      if (logFeedbackFilter === 'disliked') params.disliked = 'true';
      const res = await getAnalyticsMessages(params);
      setMessageRows(res?.data?.messages || []);
      setMessageTotal(res?.data?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLogLoading(false);
    }
  }, [apiFilters, logLimit, logPage, logSearch, logRoleFilter, logFeedbackFilter]);

  const fetchMatchBreakdown = useCallback(async () => {
    setMatchBreakdownLoading(true);
    try {
      const res = await getAnalyticsMatchBreakdown({
        ...apiFilters,
        limit: matchBreakdownLimit,
        offset: matchBreakdownPage * matchBreakdownLimit,
      });
      setMatchBreakdownRows(res?.data?.matches || []);
      setMatchBreakdownTotal(res?.data?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load match breakdown');
    } finally {
      setMatchBreakdownLoading(false);
    }
  }, [apiFilters, matchBreakdownLimit, matchBreakdownPage]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { fetchMatchBreakdown(); }, [fetchMatchBreakdown]);

  const openMatchUsers = async (m) => {
    setDrill({ view: 'users', match: m, users: null });
    setDrillLoading(true);
    try {
      const res = await getAnalyticsMatchUsers(m.match_id, {
        from: apiFilters.from,
        to: apiFilters.to,
      });
      setDrill({ view: 'users', match: res?.data?.match || m, users: res?.data?.users || [] });
    } catch (err) {
      setDrill({ view: 'users', match: m, users: [], error: err.message });
    } finally {
      setDrillLoading(false);
    }
  };

  const openUserConversation = async (matchId, userId) => {
    setDrill((d) => ({ ...d, view: 'conversation', user_id: userId, conversation: null }));
    setDrillLoading(true);
    try {
      const res = await getAnalyticsMatchUserConversation(matchId, userId);
      setDrill((d) => ({ ...d, view: 'conversation', user_id: userId, conversation: res?.data || null }));
    } catch (err) {
      setDrill((d) => ({ ...d, view: 'conversation', user_id: userId, conversation: null, error: err.message }));
    } finally {
      setDrillLoading(false);
    }
  };

  const backToUsers = () => {
    setDrill((d) => (d ? { view: 'users', match: d.match, users: d.users || [] } : null));
  };
  const closeDrill = () => setDrill(null);

  const openConversation = async (sessionId) => {
    setOpenConvo({ sessionId, data: null });
    setConvoLoading(true);
    try {
      const res = await getAnalyticsConversation(sessionId);
      setOpenConvo({ sessionId, data: res?.data || null });
    } catch (err) {
      setOpenConvo({ sessionId, data: null, error: err.message });
    } finally {
      setConvoLoading(false);
    }
  };

  const kpis = overview?.kpis || {};

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid" style={{ paddingTop: 24, paddingBottom: 24 }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>AI Chat Dashboard</Typography>
            <Typography variant="body2" color="text.secondary">
              What users are asking, how the AI is responding, and which matches drive engagement.
              Data is bounded to the last ~30 days (collection TTL).
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            onClick={() => { fetchAll(); fetchMessages(); }}
            disabled={loading || logLoading}
          >
            Refresh
          </Button>
        </Stack>

        {/* Filter bar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth size="small" label="From" type="date"
                InputLabelProps={{ shrink: true }}
                value={pendingFilters.from}
                onChange={(e) => setPendingFilters((f) => ({ ...f, from: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth size="small" label="To" type="date"
                InputLabelProps={{ shrink: true }}
                value={pendingFilters.to}
                onChange={(e) => setPendingFilters((f) => ({ ...f, to: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth size="small" label="Match ID" placeholder="e.g. 95851"
                value={pendingFilters.match_id}
                onChange={(e) => setPendingFilters((f) => ({ ...f, match_id: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth size="small" label="User ID" placeholder="e.g. 9533534"
                value={pendingFilters.user_id}
                onChange={(e) => setPendingFilters((f) => ({ ...f, user_id: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth size="small" select label="Intent"
                value={pendingFilters.intent}
                onChange={(e) => setPendingFilters((f) => ({ ...f, intent: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {[
                  'match_explainer', 'turning_point', 'prediction_engine', 'player_performance',
                  'player_comparison', 'fan_pulse', 'event_lookup', 'match_narrative',
                  'meme_mode', 'team_history', 'partnership', 'bowling_spell', 'captaincy',
                  'phase_analysis', 'what_if', 'fantasy_advisor', 'match_preview', 'general',
                ].map((it) => (<MenuItem key={it} value={it}>{it}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth size="small" select label="Model"
                value={pendingFilters.model}
                onChange={(e) => setPendingFilters((f) => ({ ...f, model: e.target.value }))}
              >
                <MenuItem value="">All</MenuItem>
                {models.map((m) => (<MenuItem key={m.model} value={m.model}>{m.model}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button fullWidth variant="contained" onClick={applyFilters} disabled={loading}>Apply</Button>
                <Button fullWidth variant="outlined" onClick={resetFilters}>Reset</Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* KPI cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}><KpiCard label="Sessions" value={formatNumber(kpis.sessions)} sub={`${formatNumber(kpis.uniqueUsers)} unique users`} /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Messages" value={formatNumber(kpis.messages)} sub={`${(kpis.avgMessagesPerSession || 0)} avg / session`} /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Unique matches" value={formatNumber(kpis.uniqueMatches)} sub={`${formatNumber(kpis.assistantMessages)} AI replies`} /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Total tokens" value={formatNumber(kpis.totalTokens)} sub={`${formatNumber(kpis.llmCalls)} LLM calls`} /></Grid>

          <Grid item xs={6} md={3}><KpiCard label="👍 Liked" value={formatNumber(kpis.liked)} sub={`${kpis.likeRate || 0}% of replies`} /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="👎 Disliked" value={formatNumber(kpis.disliked)} sub={`${kpis.dislikeRate || 0}% of replies`} /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Cache hits" value={formatNumber(kpis.cached)} sub={`${kpis.cacheHitRate || 0}% hit rate`} /></Grid>
          <Grid item xs={6} md={3}><KpiCard label="Prompt / completion" value={`${formatNumber(kpis.promptTokens)} / ${formatNumber(kpis.completionTokens)}`} sub="tokens (in / out)" /></Grid>
        </Grid>

        {/* Charts row */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, height: 360 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Daily activity</Typography>
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '85%' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="90%">
                  <LineChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="bucket" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="messages" stroke="#1976d2" name="Messages" />
                    <Line type="monotone" dataKey="uniqueUsers" stroke="#9c27b0" name="Unique users" />
                    <Line type="monotone" dataKey="sessions" stroke="#2e7d32" name="Sessions" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: 360 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Intent breakdown</Typography>
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '85%' }}>
                  <CircularProgress />
                </Box>
              ) : intents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No data in window</Typography>
              ) : (
                <ResponsiveContainer width="100%" height="90%">
                  <PieChart>
                    <Pie
                      data={intents}
                      dataKey="count"
                      nameKey="intent"
                      outerRadius={120}
                      label={(d) => d.intent}
                    >
                      {intents.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Top matches + top users */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Most-chatted matches</Typography>
              <TableContainer sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Match</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                      <TableCell align="right">Users</TableCell>
                      <TableCell align="right">Messages</TableCell>
                      <TableCell align="right">Tokens</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topMatches.map((m) => (
                      <TableRow key={m.match_id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {m.short_title || m.title || `Match ${m.match_id}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID {m.match_id} · {m.status || '—'} · {formatRelative(m.last_active)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{formatNumber(m.sessions)}</TableCell>
                        <TableCell align="right">{formatNumber(m.uniqueUsers)}</TableCell>
                        <TableCell align="right">{formatNumber(m.messages)}</TableCell>
                        <TableCell align="right">{formatNumber(m.tokens)}</TableCell>
                      </TableRow>
                    ))}
                    {topMatches.length === 0 && !loading && (
                      <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="text.secondary">No data</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Top users</Typography>
              <TableContainer sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                      <TableCell align="right">Matches</TableCell>
                      <TableCell align="right">Messages</TableCell>
                      <TableCell align="right">Tokens</TableCell>
                      <TableCell>Last active</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topUsers.map((u) => (
                      <TableRow key={u.user_id} hover>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => { setPendingFilters((f) => ({ ...f, user_id: u.user_id })); setFilters((f) => ({ ...f, user_id: u.user_id })); }}
                          >
                            {u.user_id}
                          </Button>
                        </TableCell>
                        <TableCell align="right">{formatNumber(u.sessions)}</TableCell>
                        <TableCell align="right">{formatNumber(u.uniqueMatches)}</TableCell>
                        <TableCell align="right">{formatNumber(u.messages)}</TableCell>
                        <TableCell align="right">{formatNumber(u.tokens)}</TableCell>
                        <TableCell><Typography variant="caption">{formatRelative(u.last_active)}</Typography></TableCell>
                      </TableRow>
                    ))}
                    {topUsers.length === 0 && !loading && (
                      <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary">No data</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Intent counts as a horizontal bar (alternate read of the same data) */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Intent volume + feedback</Typography>
          <Box sx={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={intents} layout="vertical" margin={{ left: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="intent" width={140} />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#1976d2" name="Total" stackId="a" />
                <Bar dataKey="liked" fill="#2e7d32" name="Liked" stackId="b" />
                <Bar dataKey="disliked" fill="#d32f2f" name="Disliked" stackId="b" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* ── MODEL ROW ─────────────────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Per-model breakdown table */}
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Model usage breakdown</Typography>
              <Typography variant="caption" color="text.secondary">
                Which LLMs actually answered. Use the like-rate / avg-tokens columns to compare quality + cost.
              </Typography>
              <TableContainer sx={{ maxHeight: 360, mt: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Model</TableCell>
                      <TableCell align="right">Replies</TableCell>
                      <TableCell align="right">Avg tokens</TableCell>
                      <TableCell align="right">Total tokens</TableCell>
                      <TableCell align="right">Avg latency</TableCell>
                      <TableCell align="right">Total latency</TableCell>
                      <TableCell align="right">👍 / 👎</TableCell>
                      <TableCell align="right">Like rate</TableCell>
                      <TableCell>Intents covered</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {models.map((m) => (
                      <TableRow key={m.model} hover>
                        <TableCell>
                          <Button
                            size="small"
                            onClick={() => { setPendingFilters((f) => ({ ...f, model: m.model })); setFilters((f) => ({ ...f, model: m.model })); }}
                          >
                            {m.model}
                          </Button>
                        </TableCell>
                        <TableCell align="right">{formatNumber(m.count)}</TableCell>
                        <TableCell align="right">{formatNumber(m.avgTokens)}</TableCell>
                        <TableCell align="right">{formatNumber(m.tokens)}</TableCell>
                        <TableCell align="right">
                          {m.avgLatencyMs != null && m.avgLatencyMs > 0 ? (
                            <Chip
                              size="small"
                              label={`${formatNumber(m.avgLatencyMs)} ms`}
                              color={m.avgLatencyMs < 3000 ? 'success' : m.avgLatencyMs < 8000 ? 'warning' : 'error'}
                              variant="outlined"
                            />
                          ) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption">
                            {m.totalLatencyMs != null && m.totalLatencyMs > 0
                              ? `${(m.totalLatencyMs / 1000).toFixed(1)}s`
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatNumber(m.liked)} / {formatNumber(m.disliked)}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={`${m.likeRate}%`}
                            color={m.likeRate > 60 ? 'success' : m.likeRate > 30 ? 'warning' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {(m.intents || []).slice(0, 6).map((i) => (
                              <Chip key={i} label={i || 'unknown'} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                            ))}
                            {(m.intents || []).length > 6 && (
                              <Chip label={`+${m.intents.length - 6}`} size="small" />
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {models.length === 0 && !loading && (
                      <TableRow><TableCell colSpan={9} align="center"><Typography variant="body2" color="text.secondary">No model data yet — restart the chat module after deploying so newly-saved messages include the model field.</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Active routing config */}
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Active routing config</Typography>
              <Typography variant="caption" color="text.secondary">
                Per-intent model picks resolved from env vars. Bold = override set, plain = falls back to default.
              </Typography>
              {routingConfig && (() => {
                // Server returns strings for default/classifier (back-compat) +
                // optional *_provider sibling. Defensive: also handle the
                // earlier object shape just in case.
                const asLabel = (val, providerSibling) => {
                  if (val == null) return '—';
                  if (typeof val === 'object') return `${val.provider || '?'}:${val.model || '?'}`;
                  return providerSibling ? `${providerSibling}:${val}` : String(val);
                };
                return (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Default:</strong>{' '}
                      <Chip size="small" label={asLabel(routingConfig.default, routingConfig.default_provider)} />
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Classifier:</strong>{' '}
                      <Chip size="small" label={asLabel(routingConfig.classifier, routingConfig.classifier_provider)} />
                    </Typography>
                    <TableContainer sx={{ maxHeight: 280 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Intent</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Model</TableCell>
                            <TableCell>Env var</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(routingConfig.intents || {}).map(([intent, info]) => (
                            <TableRow key={intent}>
                              <TableCell><Typography variant="caption">{intent}</Typography></TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={info.provider || '—'}
                                  color={info.provider === 'openai' ? 'primary'
                                    : info.provider === 'anthropic' ? 'secondary'
                                    : info.provider === 'google' ? 'success'
                                    : 'default'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ fontWeight: info.overridden ? 700 : 400, fontFamily: 'monospace' }}>
                                  {info.resolved}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                  {info.envVar}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                );
              })()}
            </Paper>
          </Grid>
        </Grid>

        {/* Intent × Model matrix — shows whether the env routing is actually directing traffic where we expect */}
        {intentModelMatrix.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Intent × Model traffic</Typography>
            <Typography variant="caption" color="text.secondary">
              Validates routing — each row shows where traffic for that intent actually went, with quality signals.
            </Typography>
            <TableContainer sx={{ maxHeight: 360, mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Intent</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Replies</TableCell>
                    <TableCell align="right">Avg tokens</TableCell>
                    <TableCell align="right">👍 / 👎</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {intentModelMatrix.map((r, i) => (
                    <TableRow key={`${r.intent}-${r.model}-${i}`} hover>
                      <TableCell><Chip size="small" label={r.intent || 'unknown'} variant="outlined" /></TableCell>
                      <TableCell><Chip size="small" label={r.model} /></TableCell>
                      <TableCell align="right">{formatNumber(r.count)}</TableCell>
                      <TableCell align="right">{formatNumber(r.avgTokens)}</TableCell>
                      <TableCell align="right">{formatNumber(r.liked)} / {formatNumber(r.disliked)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Message log */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>Messages</Typography>
            <TextField
              size="small" label="Search content" placeholder="word, name, …"
              value={logSearch}
              onChange={(e) => { setLogPage(0); setLogSearch(e.target.value); }}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size="small" select label="Role" value={logRoleFilter}
              onChange={(e) => { setLogPage(0); setLogRoleFilter(e.target.value); }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="assistant">Assistant</MenuItem>
            </TextField>
            <TextField
              size="small" select label="Feedback" value={logFeedbackFilter}
              onChange={(e) => { setLogPage(0); setLogFeedbackFilter(e.target.value); }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="liked">Liked</MenuItem>
              <MenuItem value="disliked">Disliked</MenuItem>
            </TextField>
          </Stack>

          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Intent</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Latency</TableCell>
                  <TableCell sx={{ minWidth: 360 }}>Content</TableCell>
                  <TableCell align="right">Tokens</TableCell>
                  <TableCell align="center">FB</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {logLoading && (
                  <TableRow><TableCell colSpan={11} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                )}
                {!logLoading && messageRows.map((m, i) => (
                  <TableRow key={`${m.session_id}-${i}-${m.timestamp}`} hover>
                    <TableCell><Typography variant="caption">{new Date(m.timestamp).toLocaleString()}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        label={m.role}
                        size="small"
                        color={m.role === 'assistant' ? 'primary' : 'default'}
                        variant={m.role === 'assistant' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{m.match_title || m.match_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                        {m.username || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{m.user_id}</Typography>
                    </TableCell>
                    <TableCell>{m.intent ? <Chip label={m.intent} size="small" /> : '—'}</TableCell>
                    <TableCell>
                      {m.model
                        ? <Chip label={m.model} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                        : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {m.latency_ms != null && m.latency_ms > 0 ? (
                        <Chip
                          size="small"
                          label={`${formatNumber(m.latency_ms)} ms`}
                          color={m.latency_ms < 3000 ? 'success' : m.latency_ms < 8000 ? 'warning' : 'error'}
                          variant="outlined"
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto' }}>
                        {m.content}
                      </Typography>
                      {m.cached && <Chip label="cached" size="small" sx={{ mt: 0.5 }} />}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">{formatNumber(m.token_usage?.total_tokens)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {m.liked && <ThumbUpIcon fontSize="small" color="success" />}
                      {m.disliked && <ThumbDownIcon fontSize="small" color="error" />}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" title="Open full conversation" onClick={() => openConversation(m.session_id)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!logLoading && messageRows.length === 0 && (
                  <TableRow><TableCell colSpan={11} align="center"><Typography variant="body2" color="text.secondary">No messages</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={messageTotal}
            page={logPage}
            onPageChange={(_e, p) => setLogPage(p)}
            rowsPerPage={logLimit}
            onRowsPerPageChange={(e) => { setLogLimit(parseInt(e.target.value, 10)); setLogPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Paper>

        {/* ── Match-wise breakdown ─────────────────────────────────────── */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1 }} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Match-wise breakdown</Typography>
              <Typography variant="caption" color="text.secondary">
                Every match with chat activity in the window. Click a row to see its users → click a user to read the conversation.
              </Typography>
            </Box>
          </Stack>

          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Match</TableCell>
                  <TableCell align="right">Unique users</TableCell>
                  <TableCell align="right">Messages</TableCell>
                  <TableCell align="right">Tokens</TableCell>
                  <TableCell align="right">Sessions</TableCell>
                  <TableCell>Last active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {matchBreakdownLoading && (
                  <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                )}
                {!matchBreakdownLoading && matchBreakdownRows.map((m) => (
                  <TableRow key={m.match_id} hover sx={{ cursor: 'pointer' }} onClick={() => openMatchUsers(m)}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {m.short_title || m.title || `Match ${m.match_id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID {m.match_id} · {m.status || '—'}
                          {m.team_a && m.team_b ? ` · ${m.team_a} vs ${m.team_b}` : ''}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{formatNumber(m.uniqueUsers)}</TableCell>
                    <TableCell align="right">{formatNumber(m.messages)}</TableCell>
                    <TableCell align="right">{formatNumber(m.tokens)}</TableCell>
                    <TableCell align="right">{formatNumber(m.sessions)}</TableCell>
                    <TableCell><Typography variant="caption">{formatRelative(m.last_active)}</Typography></TableCell>
                  </TableRow>
                ))}
                {!matchBreakdownLoading && matchBreakdownRows.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary">No matches in window</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={matchBreakdownTotal}
            page={matchBreakdownPage}
            onPageChange={(_e, p) => setMatchBreakdownPage(p)}
            rowsPerPage={matchBreakdownLimit}
            onRowsPerPageChange={(e) => { setMatchBreakdownLimit(parseInt(e.target.value, 10)); setMatchBreakdownPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Paper>

        {/* Match drilldown dialog (users → conversation) */}
        <Dialog open={!!drill} onClose={closeDrill} maxWidth="lg" fullWidth>
          <DialogTitle>
            {drill?.view === 'conversation' ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" onClick={backToUsers}>← Users</Button>
                <Box>
                  <Typography variant="h6">User {drill?.user_id}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {drill?.match?.short_title || drill?.match?.title || `Match ${drill?.match?.match_id}`}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Box>
                <Typography variant="h6">
                  {drill?.match?.short_title || drill?.match?.title || `Match ${drill?.match?.match_id}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ID {drill?.match?.match_id}
                  {drill?.match?.team_a && drill?.match?.team_b ? ` · ${drill?.match?.team_a} vs ${drill?.match?.team_b}` : ''}
                  {drill?.match?.status ? ` · ${drill?.match?.status}` : ''}
                </Typography>
              </Box>
            )}
          </DialogTitle>
          <DialogContent dividers>
            {drillLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
            {drill?.error && <Alert severity="error" sx={{ mb: 2 }}>{drill.error}</Alert>}

            {/* Users view */}
            {!drillLoading && drill?.view === 'users' && (
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell align="right">Sessions</TableCell>
                      <TableCell align="right">Messages</TableCell>
                      <TableCell align="right">Tokens</TableCell>
                      <TableCell align="right">LLM calls</TableCell>
                      <TableCell>First active</TableCell>
                      <TableCell>Last active</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(drill?.users || []).map((u) => (
                      <TableRow
                        key={u.user_id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => openUserConversation(drill.match.match_id, u.user_id)}
                      >
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{u.user_id}</Typography></TableCell>
                        <TableCell align="right">{formatNumber(u.sessions)}</TableCell>
                        <TableCell align="right">{formatNumber(u.messages)}</TableCell>
                        <TableCell align="right">{formatNumber(u.tokens)}</TableCell>
                        <TableCell align="right">{formatNumber(u.llmCalls)}</TableCell>
                        <TableCell><Typography variant="caption">{formatRelative(u.first_active)}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{formatRelative(u.last_active)}</Typography></TableCell>
                        <TableCell>
                          <IconButton size="small" title="Open conversation"><OpenInNewIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(drill?.users || []).length === 0 && (
                      <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary">No users for this match in the selected window</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Conversation view */}
            {!drillLoading && drill?.view === 'conversation' && drill?.conversation && (
              <Box>
                {/* Per-conversation summary chip strip */}
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                  <Chip size="small" label={`${formatNumber(drill.conversation.summary?.messages)} msgs`} />
                  <Chip size="small" label={`${formatNumber(drill.conversation.summary?.assistantMessages)} replies`} />
                  <Chip size="small" label={`${formatNumber(drill.conversation.summary?.tokens)} tokens`} />
                  <Chip
                    size="small"
                    label={`avg response ${drill.conversation.summary?.avgResponseTimeMs != null ? `${drill.conversation.summary.avgResponseTimeMs} ms` : '—'}`}
                  />
                  <Chip size="small" label={`${(drill.conversation.sessions || []).length} sessions`} />
                </Stack>

                {(drill.conversation.messages || []).map((m, i) => (
                  <Box key={i} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                      <Chip label={m.role} size="small" color={m.role === 'assistant' ? 'primary' : 'default'} />
                      {m.intent && <Chip label={`intent: ${m.intent}`} size="small" variant="outlined" />}
                      {m.model && <Chip label={`model: ${m.model}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />}
                      {m.token_usage?.total_tokens != null && (
                        <Chip label={`${formatNumber(m.token_usage.total_tokens)} tok`} size="small" />
                      )}
                      {m.role === 'assistant' && m.response_time_ms != null && (
                        <Chip
                          label={`${m.response_time_ms} ms`}
                          size="small"
                          color={m.response_time_ms < 3000 ? 'success' : m.response_time_ms < 8000 ? 'warning' : 'error'}
                        />
                      )}
                      {m.cached && <Chip label="cached" size="small" />}
                      {m.liked && <ThumbUpIcon fontSize="small" color="success" />}
                      {m.disliked && <ThumbDownIcon fontSize="small" color="error" />}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {new Date(m.timestamp).toLocaleString()}
                      </Typography>
                    </Stack>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
                    </Paper>
                  </Box>
                ))}
                {(drill.conversation.messages || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">No messages found.</Typography>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Conversation detail dialog */}
        <Dialog open={!!openConvo} onClose={() => setOpenConvo(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            Conversation {openConvo?.sessionId}
            {openConvo?.data?.match_short_title && (
              <Typography variant="body2" color="text.secondary">
                {openConvo.data.match_short_title} · user {openConvo.data.user_id}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent dividers>
            {convoLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
            {openConvo?.error && <Alert severity="error">{openConvo.error}</Alert>}
            {openConvo?.data?.messages?.map((m, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip label={m.role} size="small" color={m.role === 'assistant' ? 'primary' : 'default'} />
                  {m.intent && <Chip label={m.intent} size="small" variant="outlined" />}
                  {m.model && <Chip label={m.model} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />}
                  {m.cached && <Chip label="cached" size="small" />}
                  {m.liked && <ThumbUpIcon fontSize="small" color="success" />}
                  {m.disliked && <ThumbDownIcon fontSize="small" color="error" />}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {new Date(m.timestamp).toLocaleString()}
                  </Typography>
                </Stack>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
                </Paper>
              </Box>
            ))}
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </Component>
  );
}
